import { makeAutoObservable } from 'mobx'
import { cloneDeep, forEach, groupBy, isEmpty, keyBy, range } from '@dt/fe-utils/dist/lodash'
import { toDecimal, pk as doPk } from '@dt/fe-utils'
import productMobx from './product.mobx'
import cartMobx, { createCartItem, getViewItems } from './cart.mobx'
import { convertDoModifierGroupsToModifierGroups, convertModifierGroupsToDoModifierGroups, createFkey } from './center/cookware'
import { restaurantConfig, doProductMobx, productSaleMobx, discountMobx, feeMobx } from '..'

type TDoComboType = 'FixedCombo' | 'OptionalCombo'

const getDefaultCombo = () => {
  return {
    type: '',
    no: '',
    name: '',
    pic: '',
    cnt: 1,
    scales: [],
    modifierGroups: [],
    scaleNo: '',
    isTogo: 0 as 0 | 1,
    isScaleModifierGroup: false,
    startTimeOffset: 0,
    itemPlan: null,
    taxCategoryId: 0,
    isTaxFree: false,
    allergys: [],
    tags: [],
    productGroups: []
  }
}

const getDefaultComboExt = () => {
  return {}
}

/** 拆分购物车套餐内复数起售份数(salesCnt)的相同加料的子餐品 */
const convertProductGroupDetails = (productGroupDetails: IViewComboCartItem[]) => {
  const details: IViewComboCartItem[] = []
  forEach(productGroupDetails, (product: any) => {
    if (product.cnt > product.salesCnt) {
      // 项数
      const selectCnt = product.cnt / product.salesCnt
      return forEach(range(selectCnt), (index) => {
        details.push({
          ...product,
          cnt: product.salesCnt,
          addPrice: product.addPrice / selectCnt,
          pk: index === 0 ? product.pk : `${product.pk}_${index}`
        })
      })
    } else {
      details.push(product)
    }
  })
  return details
}

/**
 * 操作套餐的模块
 * 当一个套餐在放入购物车前的临时状态用此模块，类似于操作餐品又有自己的购物车
 */
class DoCombo {
  constructor () {
    makeAutoObservable(this)
  }

  /** 套餐信息，包含了原始套餐里的已选餐品 */
  doCombo: IDoCombo = getDefaultCombo()

  /** 额外信息 */
  doComboExt: any = getDefaultComboExt()

  /** 套餐类型 */
  doComboType: TDoComboType = 'FixedCombo'

  /** 套餐的餐品组，类似于菜单，但是是以groupNo作为分组 */
  productGroups: IViewComboGroup[] = []

  // group map
  get groupNoToGroupMap () {
    const groupNoToGroupMap: any = {}
    this.productGroups?.forEach((group) => {
      groupNoToGroupMap[group.no] = group
    })
    return groupNoToGroupMap
  }

  // group product pk map
  get groupNoProductPkReadyMap () {
    const groupNoProductPkReadyMap: any = {}
    this.doCombo.productGroups?.forEach((group) => {
      groupNoProductPkReadyMap[group.no] = groupNoProductPkReadyMap[group.no] || {}
      group.productGroupDetails.forEach(product => {
        let isReady = false
        if (!product.modifierGroups.length) {
          isReady = true
        } else if (product?.modifierGroups.every((doComponent: IDoModifierGroup) => doComponent.isReady)) {
          isReady = true
        }
        groupNoProductPkReadyMap[group.no][product.pk] = isReady
      })
    })
    return groupNoProductPkReadyMap
  }

  // product not ready pk array
  get notReadyProduct () {
    const arr: IViewComboCartItem[] = []
    this.doCombo.productGroups?.forEach((group) => {
      group.productGroupDetails.forEach(product => {
        let isReady = false
        if (!product.modifierGroups.length) {
          isReady = true
        } else if (product?.modifierGroups.every((doComponent: IDoModifierGroup) => doComponent.isReady)) {
          isReady = true
        }
        if (!isReady) {
          arr.push(product)
        }
      })
    })
    return arr
  }

  /** 套餐选择过程中的计算结果集, gps groupNo_productNo_scaleNo */
  get doComboResult () {
    const productNoToCntMap: any = {}
    const productScaleNoToCntMap: any = {}
    const gpsNoToCntMap: {
      [gps: string]: number;
    } = {}
    const gpsNoToTypeCntMap: {
      [gps: string]: number;
    } = {}
    let groupNoToCntMap: any = {}
    // 按照项数量，只要子餐品有数量，就当作，而不是选中子餐品数量cnt
    const groupNoToTypeCntMap: any = {}
    const groupNoToReadyMap: any = {}
    const groupNoProductPkReadyMap = this.groupNoProductPkReadyMap
    let price = 0
    let actualPrice = 0
    let modifierPrice = 0
    let addPrice = 0
    let isReady = false
    let totalCnt = 0
    if (this.doCombo.scaleNo) {
      const doScale = this.doCombo.scales.find((scale) => scale.no === this.doCombo.scaleNo)
      if (doScale) {
        price = doScale.price || 0
        actualPrice = doScale.actualPrice || 0
      }
    }
    if (typeof (this.doCombo.openPriceX) !== 'undefined' && this.doCombo.openPriceX !== -1) {
      price = this.doCombo.openPriceX || 0
    }
    const keyByProductGroups = keyBy(this.productGroups, 'no')
    this.doCombo.productGroups?.forEach(group => {
      group.productGroupDetails.forEach(product => {
        addPrice += (product?.addPrice || 0)
        modifierPrice += product.modifierPrice * product.cnt
        productNoToCntMap[product.productNo] = (productNoToCntMap[product.productNo] || 0) + product.cnt
        productScaleNoToCntMap[`${product.productNo}_${product.scaleNo}`] = (productScaleNoToCntMap[`${product.productNo}_${product.scaleNo}`] || 0) + product.cnt
        groupNoToCntMap[product.groupNo] = (groupNoToCntMap[product.groupNo] || 0) + product.cnt
        groupNoToTypeCntMap[product.groupNo] = (groupNoToTypeCntMap[product.groupNo] || 0) + product.cnt / product.cnt
        gpsNoToCntMap[product.groupNo + '_' + product.productNo + '_' + product.scaleNo] = (gpsNoToCntMap[product.groupNo + '_' + product.productNo + '_' + product.scaleNo] || 0) + product.cnt
        gpsNoToTypeCntMap[product.groupNo + '_' + product.productNo + '_' + product.scaleNo] = (gpsNoToTypeCntMap[product.groupNo + '_' + product.productNo + '_' + product.scaleNo] || 0) + product.cnt / product.cnt
        totalCnt = totalCnt + product.cnt
      })
      // TODO: 2023/05/09自选套餐增加起售份数, 对应自选套餐组购物车 相同餐品数量 / 对应起售份数 === 自选套餐规则的项数
      if (this.doComboType === 'OptionalCombo') {
        const scaleProductGroupDetails = group.productGroupDetails.map(item => ({ ...item, productScaleStr: item.productNo + '-' + item.scaleNo }))
        const groupByProductGroupDetails = groupBy(scaleProductGroupDetails, 'productScaleStr')
        const scaleProductGroupSelectDetails = (keyByProductGroups[group.no]?.productGroupDetails || []).map(item => ({ ...item, productScaleStr: item.productNo + '-' + item.scaleNo }))
        const keyByProductDetail = keyBy(scaleProductGroupSelectDetails, 'productScaleStr')
        let optionGroupNoToCntMap = 0
        forEach(groupByProductGroupDetails, (productList: IViewComboCartItem[], productScaleStr) => {
          const product = keyByProductDetail[productScaleStr]
          if (product) {
            const cnt = (productList.reduce((acc, cur) => (cur?.cnt || 1) + acc, 0) || 1) / (product.cnt || 1)
            optionGroupNoToCntMap += cnt
          }
        })
        groupNoToCntMap[group.no] = optionGroupNoToCntMap
      }
      const groupIsReady = groupNoToTypeCntMap[group.no] === group.selectMin
      const productIsReady = group.productGroupDetails.every(product => groupNoProductPkReadyMap[group.no][product.pk])
      if (this.doComboType === 'FixedCombo') {
        groupNoToReadyMap[group.no] = productIsReady
      } else if (this.doComboType === 'OptionalCombo') {
        groupNoToReadyMap[group.no] = groupIsReady && productIsReady
      }
    })
    if (restaurantConfig.app === 'pos') {
      isReady = this.doComboType === 'OptionalCombo' ? this.productGroups?.every(group => group.selectMin === groupNoToCntMap[group.no]) : this.productGroups?.every(group => groupNoToReadyMap[group.no])
    } else {
      isReady = this.productGroups?.every(group => groupNoToReadyMap[group.no])
    }
    // forEach(addPriceMap, (value: number) => {
    //   addPrice += value;
    // })
    const totalPrice = price + modifierPrice + addPrice
    const totalActualPrice = actualPrice + modifierPrice + addPrice
    const taxPrice = feeMobx.calTaxPrice(this.doCombo.taxCategoryId, totalActualPrice)
    const isCanTogo = !!this.doCombo?.productGroups?.every(group => group?.productGroupDetails.every(product => !!product.isCanTogo))
    return {
      productNoToCntMap,
      productScaleNoToCntMap,
      groupNoToCntMap,
      groupNoToTypeCntMap,
      groupNoToReadyMap,
      gpsNoToCntMap,
      gpsNoToTypeCntMap,
      price, // 餐品价
      modifierPrice, // 加料价
      isReady,
      totalPrice, // 总价
      totalActualPrice, // 实际总价（按优惠价格计算）
      taxPrice,
      isCanTogo,
      totalCnt // 选中的子餐品总数
    }
  }

  /** 默认置入可置入的餐品 */
  private placeProducts () {
    this.productGroups?.forEach((group: IViewComboGroup) => {
      group.productGroupDetails.forEach((product: IViewComboGroupProduct) => {
        if (this.doComboType === 'FixedCombo') {
          let cnt = product.cnt || 0
          for (let i = 0; i < cnt; i++) {
            doProductMobx.pick({ productNo: product.no, scaleNo: product.scaleNo, groupNo: product.groupNo, isCombo: true })
            this.addToCombo(doProductMobx.doProduct)
          }
        } else if (this.doComboType === 'OptionalCombo') {
          if (product.isDefault && (isEmpty(product.modifierGroups) || restaurantConfig.defaultOptionalComboPlace) && product.scales?.length === 1) {
            if (product.saleCnt !== 0) {
              doProductMobx.pick({ productNo: product.no, scaleNo: product.scaleNo, groupNo: product.groupNo, addPrice: product.addPrice, initAdd: true, salesCnt: product?.cnt || 1, isCombo: true })
              if (!doProductMobx.checkInvalid() && doProductMobx.doProductResult.isReady) {
                this.addToCombo(doProductMobx.doProduct)
              }
            }
          }
        }
      })
    })
  }

  /** 将一个套餐放入到可操作套餐中，之后可以在doComboMobx访问 */
  pick ({ product, productNo, cartItemPk, isOrderSwapBuy, initAdd }: { product?: IViewProduct; productNo?: string; cartItemPk?: string; isOrderSwapBuy?: boolean; initAdd?: false }) {
    let productGroups: IViewComboGroup[] = []
    const hasDiscount = restaurantConfig.app === 'mobile' && product && isOrderSwapBuy // 已有营销价格
    if (productNo || product) {
      let viewProduct = product || (productNo ? productMobx.noToViewProductMap[productNo] : undefined)
      if (viewProduct) {
        if (!viewProduct.isCombo) {
          console.error('this product is not a combo')
          return
        }
        const scale = viewProduct.scales[0]
        const modifierGroups = convertModifierGroupsToDoModifierGroups(viewProduct?.no, scale?.modifierGroups || viewProduct.modifierGroups)
        const scales = cloneDeep(viewProduct.scales).map((scale: IScale) => {
          // 处理金额因为openPrice是一个可变金额所有需要另一个参数price
          if (scale.isOpenPrice) {
            scale.price = undefined
          } else if (!hasDiscount) {
            // 对接营销价格
            if (scale.price) {
              if (discountMobx.itemDiscountMap && discountMobx.itemDiscountMap[viewProduct?.no + '_' + scale.no]) {
                scale.discountPrice = discountMobx.itemDiscountMap[viewProduct?.no + '_' + scale.no].off
                scale.actualPrice = toDecimal(scale.price - scale.discountPrice) as number
              } else {
                scale.discountPrice = 0
                scale.actualPrice = scale.price
              }
            }
          }
          return scale
        })
        const doProduct = {
          type: viewProduct.type,
          id: viewProduct.id,
          no: viewProduct.no,
          name: viewProduct.name,
          pic: viewProduct.pic,
          pics: viewProduct.pics,
          cnt: initAdd ? viewProduct.salesCnt : 1,
          scaleNo: scale ? scale.no : null, // 默认选中第一个可售的规格
          scales,
          isTogo: cartMobx.cartResult.isTogo, // 来自购物车的默认togo状态
          isCanTogo: viewProduct.isCanTogo,
          isSingleSale: viewProduct.isSingleSale,
          startTimeOffset: 0,
          isOpenPrice: viewProduct.isOpenPrice,
          isScaleOpenPrice: viewProduct.isScaleOpenPrice,
          modifierGroups,
          allergys: viewProduct.allergys,
          tags: viewProduct.tags,
          intro: viewProduct.intro,
          maxSalesCnt: viewProduct.maxSalesCnt,
          saleCnt: viewProduct.saleCnt,
          saleTotalCnt: viewProduct.saleTotalCnt,
          statSaleCnt: viewProduct.statSaleCnt,
          scaleGroupName: viewProduct.scaleGroup.name,
          picBig: viewProduct.picBig,
          picWidth: viewProduct.picWidth,
          picHeight: viewProduct.picHeight,
          itemPlan: null,
          isScaleModifierGroup: viewProduct.scales.some(scale => !!scale.modifierGroups),
          taxCategoryId: viewProduct.taxCategoryId,
          isTaxFree: false,
          productGroups: viewProduct.productGroups?.map(group => {
            const productGroupDetails: IViewComboCartItem[] = []
            return { ...group, productGroupDetails }
          }) as IViewComboCartGroup[],
          isOrderSwapBuy,
          showType: viewProduct.showType,
        } as IDoCombo
        this.doCombo = doProduct
        this.doComboType = viewProduct.type as TDoComboType
        if (viewProduct.productGroups) {
          productGroups = viewProduct.productGroups
        }
      }
    } else if (cartItemPk) {
      const viewCartItem = cartMobx.pkToViewCartItemMap[cartItemPk]
      if (!viewCartItem.productGroups) {
        console.error('Use doProduct pick the product cart item')
        return
      }
      let viewProduct = productMobx.noToViewProductMap[viewCartItem.productNo]
      if (product) {
        viewProduct = product
      }
      if (viewProduct) {
        const scale = viewProduct.scales[0]
        const modifierGroups = convertModifierGroupsToDoModifierGroups(viewProduct?.no, scale.modifierGroups || viewProduct.modifierGroups)
        const scales = cloneDeep(viewProduct.scales).map((scale: IScale) => {
          // 处理金额因为openPrice是一个可变金额所有需要另一个参数price
          if (scale.isOpenPrice) {
            scale.price = undefined
          } else if (!hasDiscount) {
            // 对接营销价格
            if (scale.price) {
              if (discountMobx.itemDiscountMap && discountMobx.itemDiscountMap[viewProduct.no + '_' + scale.no]) {
                scale.discountPrice = discountMobx.itemDiscountMap[viewProduct.no + '_' + scale.no].off
                scale.actualPrice = toDecimal(scale.price - scale.discountPrice) as number
              } else {
                scale.discountPrice = 0
                scale.actualPrice = scale.price
              }
            }
          }
          return scale
        })
        const doProduct = {
          type: viewCartItem.type,
          no: viewCartItem.productNo,
          name: viewCartItem.name,
          pic: viewProduct.pic,
          cnt: viewCartItem.cnt,
          scaleNo: viewCartItem.scaleNo, // 默认选中第一个可售的规格
          isTogo: viewCartItem.isTogo, // 来自购物车项的逆向
          isCanTogo: viewCartItem.isCanTogo,
          isSingleSale: viewProduct.isSingleSale,
          scales,
          scaleGroupName: viewProduct.scaleGroup.name,
          modifierGroups,
          startTime: viewCartItem.startTime,
          pickTimeRange: viewCartItem.pickTimeRange,
          startTimeOffset: viewCartItem.startTimeOffset || 0,
          pk: viewCartItem.pk,
          intro: viewCartItem.intro,
          saleCnt: viewProduct.saleCnt,
          saleTotalCnt: viewProduct.saleTotalCnt,
          statSaleCnt: viewProduct.statSaleCnt,
          remark: viewCartItem.remark,
          isOpenPrice: viewProduct.isOpenPrice,
          isScaleOpenPrice: viewProduct.isScaleOpenPrice,
          picBig: viewProduct.picBig,
          picWidth: viewProduct.picWidth,
          picHeight: viewProduct.picHeight,
          allergys: viewProduct.allergys,
          tags: viewProduct.tags,
          // TODO: 考虑存储的节点
          itemPlan: (discountMobx.discountAction.itemPlan && discountMobx.discountAction.itemPlan[viewCartItem.pk]) || (typeof (viewCartItem.openPriceX) !== 'undefined' && viewCartItem.openPriceX !== -1 && { type: 'changeFee', value: viewCartItem.openPriceX }) || null,
          isScaleModifierGroup: viewProduct.scales.some(scale => !!scale.modifierGroups),
          addUserId: viewCartItem.addUserId,
          addUserName: viewCartItem.addUserName,
          addUserAvatar: viewCartItem.addUserAvatar,
          taxCategoryId: viewProduct.taxCategoryId,
          isTaxFree: !!feeMobx.taxFreeAction[viewCartItem.pk],
          swapBuyProducts: viewCartItem.swapBuyProducts,
          timestamp: viewCartItem.timestamp,
          productGroups: viewCartItem.productGroups.map((group) => {
            return {
              ...group,
              productGroupDetails: convertProductGroupDetails(group.productGroupDetails)
            }
          }),
          fullItemNFreeProducts: viewCartItem.fullItemNFreeProducts,
          seat: viewCartItem.seat,
          isOrderSwapBuy: isOrderSwapBuy || viewCartItem.isOrderSwapBuy
        } as IDoCombo
        if (typeof (viewCartItem.openPriceX) !== 'undefined' && viewCartItem.openPriceX !== -1) {
          doProduct.openPriceX = viewCartItem.openPriceX
        }
        this.doCombo = doProduct
        this.doComboType = viewProduct.type as TDoComboType
        if (viewProduct.productGroups) {
          productGroups = viewProduct.productGroups
        }
      }
    } else {
      this.doCombo = getDefaultCombo()
    }
    // 将套餐置入到数据中
    this.productGroups = productGroups?.map(group => {
      group.productGroupDetails = group.productGroupDetails.map(item => {
        return { ...item, groupNo: group.no } as IViewComboGroupProduct
      })
      return group
    })
    if (productNo) {
      this.placeProducts()
    }
  }

  /** 额外信息 */
  pickExt (data?: {}) {
    if (data) {
      this.doComboExt = data
    } else {
      this.doComboExt = getDefaultComboExt()
    }
  }

  /** 将一个套餐中的餐品放入到套餐购物车中 */
  addToCombo (doProduct: IDoProduct) {
    const type = doProduct.type
    const groupNo = doProduct.groupNo
    const productNo = doProduct.no
    const scaleNo = doProduct.scaleNo
    const remark = doProduct.remark || ''
    const cnt = doProduct.cnt
    const pk = doProduct.pk || doPk()
    const salesCnt = doProduct.salesCnt || 1
    if (!scaleNo) {
      console.error('scale no is null')
      return
    }
    if (!groupNo) {
      console.error('group No is null')
      return
    }
    const modifierGroups = convertDoModifierGroupsToModifierGroups(doProduct.modifierGroups)
    const addUserId = doProduct.addUserId || restaurantConfig?.member?.key || ''
    const isTogo = restaurantConfig.app !== 'pos' && restaurantConfig.codeType !== 'brand' ? (cartMobx.info.eatType !== 'dinein' ? 1 : 0) : this.doCombo.isTogo
    const fkey = createFkey({ productNo, scaleNo, modifierGroups, addUserId, isTogo, isSeat: doProduct.seat || '-1', openPriceX: doProduct.openPriceX, remark })
    const doScale = doProduct.scales.find((scale) => scale.no === scaleNo)
    const price = (doScale && doScale.price) || 0
    const startTimeOffset = this.doCombo.startTimeOffset
    const cartItem = createCartItem({ cnt, type, productNo, price, fkey, scaleNo, modifierGroups, isTogo, remark, startTimeOffset }) as ICartItem
    const viewComboItem = getViewItems([cartItem])[0]
    const newComboItem: IViewComboCartItem = { ...viewComboItem, groupNo, addPrice: doProduct.addPrice, salesCnt }
    const pgIdx = this.doCombo.productGroups?.findIndex(group => groupNo === group.no)
    const pgdIdx = this.doCombo.productGroups[pgIdx]?.productGroupDetails.findIndex(product => pk === product.pk)
    if (~pgdIdx) {
      this.doCombo.productGroups[pgIdx]?.productGroupDetails.splice(pgdIdx, 1, newComboItem)
    } else {
      this.doCombo.productGroups[pgIdx]?.productGroupDetails.push((newComboItem))
    }
    this.doCombo = {
      ...this.doCombo
    }
  }

  /** 更改套餐购物车中的餐品数量 */
  modify (comboItem: IViewComboCartItem) {
    const { type, groupNo, productNo, price, scaleNo, modifierGroups, isTogo, cnt, remark, startTimeOffset, startTime, seat, isOrderSwapBuy, productGroups, addUserId, openPriceX } = comboItem
    // 重新生成fkey
    const newFkey = createFkey({ productNo, scaleNo, modifierGroups, addUserId, isTogo, startTime, isSeat: seat || '-1', isOrderSwapBuy, productGroups, openPriceX: openPriceX, remark })
    const cartItem = createCartItem({ type, productNo, price, fkey: newFkey, scaleNo, cnt, modifierGroups, isTogo, remark, startTimeOffset: startTimeOffset || 0 }) as ICartItem
    const viewComboItem = getViewItems([cartItem])[0]
    const newComboItem: IViewComboCartItem = { ...viewComboItem, groupNo }
    const pgIdx = this.doCombo.productGroups?.findIndex(group => comboItem.groupNo === group.no)
    const pgdIdx = this.doCombo.productGroups[pgIdx]?.productGroupDetails.findIndex(product => comboItem.pk === product.pk)
    if (~pgdIdx) {
      this.doCombo.productGroups[pgIdx]?.productGroupDetails.splice(pgdIdx, 1, newComboItem)
    }
  }

  /** 反选选中的餐品 */
  unselect (comboProduct: IViewComboGroupProduct | IDoProduct) {
    const groupNo = comboProduct.groupNo
    const productNo = comboProduct.no
    const scaleNo = comboProduct.scaleNo
    const pgIdx = this.doCombo.productGroups?.findIndex(group => groupNo === group.no)
    const comboItem = this.doCombo.productGroups[pgIdx]?.productGroupDetails.find(product => productNo === product.productNo && scaleNo === product.scaleNo)
    if (comboItem) {
      this.remove(comboItem)
    }
  }

  // 2023/05/09 :自选套餐增加子餐品按口味加料拆分，增加按批量删除购物出相同餐品（不包含加料）
  unSelectOptionProduct (comboProduct: IViewComboGroupProduct | IDoProduct) {
    const groupNo = comboProduct.groupNo
    const productNo = comboProduct?.no
    const scaleNo = comboProduct.scaleNo
    this.doCombo.productGroups?.forEach(group => {
      if (group.no === groupNo) {
        group.productGroupDetails = group.productGroupDetails.filter((item) => item.scaleNo === scaleNo && item.productNo === productNo)
      }
    })
    return
  }

  // 移除购物车自选套餐
  removeOptionProduct (comboItem: IViewComboCartItem) {
    const groupNo = comboItem.groupNo
    const productNo = comboItem.productNo
    const scaleNo = comboItem.scaleNo
    this.doCombo.productGroups?.forEach(group => {
      if (group.no === groupNo) {
        group.productGroupDetails = group.productGroupDetails.filter((item) => !(item.scaleNo === scaleNo && item.productNo === productNo))
      }
    })
    return
  }

  /** 移除套餐购物车中的餐品 */
  remove (comboItem: IViewComboCartItem) {
    const groupNo = comboItem.groupNo
    const pk = comboItem.pk
    const pgIdx = this.doCombo.productGroups?.findIndex(group => groupNo === group.no)
    const pgdIdx = this.doCombo.productGroups[pgIdx]?.productGroupDetails.findIndex(product => pk === product.pk)
    if (~pgdIdx) {
      this.doCombo.productGroups[pgIdx]?.productGroupDetails.splice(pgdIdx, 1)
    }
  }

  /** 清空套餐购物车中的餐品 */
  clear () {
    this.doCombo.productGroups?.forEach(group => {
      group.productGroupDetails = []
    })
  }

  /** 重置套餐购物车中的餐品 */
  reset () {
    this.doCombo.productGroups?.forEach(group => {
      group.productGroupDetails = []
    })
    this.placeProducts()
  }

  /** 选择加料项 */
  selectModifier (pk: string, groupNo: string, productNo: string, scaleNo: string, modifierGroupNo: string, modifierGroupItemNo: string) {
    const doGroup = this.doCombo.productGroups?.find((group: IViewComboCartGroup) => group.no === groupNo)
    if (!doGroup) {
      return
    }
    const doProduct = doGroup?.productGroupDetails.find((product: IViewComboCartItem) => product.pk === pk && product.productNo === productNo && product.scaleNo === scaleNo)
    if (!doProduct) {
      return
    }
    const doComponent = doProduct.modifierGroups.find((modifierGroup) => modifierGroup.no === modifierGroupNo)
    if (doComponent) {
      const doComponentItem = doComponent.modifiers.find(item => item.no === modifierGroupItemNo)
      if (doComponentItem && !doComponentItem.cnt && doComponent.cnt === doComponent.selectMax && !((doComponent.selectMin === 1 || doComponent.selectMin === 0) && doComponent.selectMax === 1)) {
        return
      }
      // 优化为单选模式
      if (doComponent.isReady && (doComponent.selectMin === 1 && doComponent.selectMax === 1)) {
        const selectedItem = doComponent.modifiers.find((item) => item.cnt === 1)
        if (selectedItem) {
          selectedItem.cnt = 0
          if (doComponentItem) {
            doComponentItem.cnt = 1
          }
        }
      } else if (doComponent.selectMin === 0 && doComponent.selectMax === 1) {
        const selectedItem = doComponent.modifiers.find((item) => item.cnt === 1)
        if (selectedItem) {
          selectedItem.cnt = 0
          if (doComponentItem && selectedItem.no !== doComponentItem.no) {
            doComponentItem.cnt = 1
          }
        } else if (doComponentItem) {
          doComponentItem.cnt = 1
        }
      } else {
        // 可叠加模式
        if (doComponentItem) {
          if (doComponentItem.cnt) {
            doComponentItem.cnt = 0
            doComponent.cnt--
          } else {
            doComponentItem.cnt = 1
            doComponent.cnt++
          }
        }
      }
      if (doComponent.cnt >= doComponent.selectMin && doComponent.cnt <= doComponent.selectMax) {
        doComponent.isReady = true
      } else {
        doComponent.isReady = false
      }
    }
    this.modify(doProduct)
  }

  /** 增加口味项（针对加减模式） */
  addModifier (pk: string, groupNo: string, productNo: string, scaleNo: string, modifierGroupNo: string, modifierGroupItemNo: string, isSupportChoice = false): 'NONE' | 'MODIFIER' | 'MODIFIERGROUP_ITEM' | 'MODIFIERGROUP_CNT' | true {
    const doGroup = this.doCombo.productGroups?.find((group: IViewComboCartGroup) => group.no === groupNo)
    if (!doGroup) {
      return 'NONE'
    }
    const doProduct = doGroup?.productGroupDetails.find((product: IViewComboCartItem) => product.pk === pk && product.productNo === productNo && product.scaleNo === scaleNo)
    if (!doProduct) {
      return 'NONE'
    }
    const doComponent = doProduct.modifierGroups.find((modifierGroup) => modifierGroup.no === modifierGroupNo)
    if (!doComponent) {
      return 'NONE'
    }
    const doComponentItem = doComponent.modifiers.find(item => item.no === modifierGroupItemNo)
    if (!doComponentItem) {
      return 'NONE'
    }
    // 支持单选混杂
    if (isSupportChoice && doComponentItem.cnt === 1 && doComponentItem.maxCnt === 1) {
      doComponentItem.cnt = 0
      doComponent.cnt--
      doComponent.itemCnt--
    } else {
      if (doComponentItem.cnt >= doComponentItem.maxCnt && doComponentItem.maxCnt !== -1) {
        return 'MODIFIER'
      }
      // 组中选中的项的份数不得大于限制份数
      if (!doComponentItem.cnt && doComponent.itemCnt === doComponent.selectMax) {
        return 'MODIFIERGROUP_ITEM'
      }
      // 选择项的总数不能大于限制数
      if (doComponent.maxCnt !== -1 && doComponent.cnt === doComponent.maxCnt) {
        return 'MODIFIERGROUP_CNT'
      }
      // 可叠加模式
      if (!doComponentItem.cnt) {
        doComponent.itemCnt++
      }
      doComponentItem.cnt++
      doComponent.cnt++
    }
    doComponent.isReady = doComponent.itemCnt >= doComponent.selectMin && doComponent.itemCnt <= doComponent.selectMax && (doComponent.cnt <= doComponent.maxCnt || doComponent.maxCnt === -1)
    this.modify(doProduct)
    return true
  }

  /** 减少口味项（针对加减模式） */
  reduceModifier (pk: string, groupNo: string, productNo: string, scaleNo: string, modifierGroupNo: string, modifierGroupItemNo: string) {
    const doGroup = this.doCombo.productGroups?.find((group: IViewComboCartGroup) => group.no === groupNo)
    if (!doGroup) {
      return
    }
    const doProduct = doGroup?.productGroupDetails.find((product: IViewComboCartItem) => product.pk === pk && product.productNo === productNo && product.scaleNo === scaleNo)
    if (!doProduct) {
      return
    }
    const doComponent = doProduct.modifierGroups.find((modifierGroup) => modifierGroup.no === modifierGroupNo)
    if (!doComponent) {
      return false
    }
    const doComponentItem = doComponent.modifiers.find(item => item.no === modifierGroupItemNo)
    if (!doComponentItem) {
      return false
    }
    if (!doComponentItem.cnt) {
      return false
    }
    // 可叠加模式
    if (doComponentItem.cnt === 1) {
      doComponent.itemCnt--
    }
    doComponentItem.cnt--
    doComponent.cnt--
    if (doComponent.cnt < doComponent.selectMin) {
      doComponent.isReady = false
    }
    this.modify(doProduct)
  }

  /** 设置套餐备注 */
  setRemark (remark: string) {
    this.doCombo.remark = remark
  }

  /** 设置套餐数量 */
  setCnt (cnt: number) {
    this.doCombo.cnt = cnt
  }

  /** 设置延迟时间 */
  setStartTimeOffset (time: number) {
    this.doCombo.startTimeOffset = time
  }

  /** 检测当前的餐品选择是否可通过验证（有别于isReady，当前主要处理限售计算问题） */
  checkInvalid (): number {
    // const isPayModeOrderFirst = !isEmpty(orderMobx?.order?.ordersId) && orderMobx?.order?.payMode?.toString() === '0' && location.hash.includes('/menu')
    if (!productSaleMobx.isInLimit) {
      return 0
    }
    // 当前scaleNo为0，当时售卖中餐品级记录的是-1
    const theProductNo = this.doCombo.no
    const theScaleNo = this.doCombo.scaleNo
    const productSaleStaus = productSaleMobx.menuProductNoToCntMap[theProductNo]
    const productScales = productMobx.noToViewProductMap?.[theProductNo]?.scales?.filter(scale => scale.status !== 'disable')
    const cartItemCnt = cartMobx.viewCartItems.find((item: any) => item.pk === this.doCombo.pk)?.cnt || 0
    if (productSaleStaus && productSaleStaus.cnt > 0) {
      const currentCount = (cartMobx.cartResult.allProductNoToCntMap[theProductNo] || 0) + this.doCombo.cnt - cartItemCnt
      if (currentCount > productSaleStaus.cnt) {
        return restaurantConfig.app === 'pos' ? currentCount - productSaleStaus.cnt : 9001
      }
    }
    if (productScales?.length > 1) { // 多规格餐品，判断规格级
      const scaleSaleStaus = productSaleMobx.scaleNoToCntMap[theProductNo + '_' + theScaleNo]
      if (scaleSaleStaus && scaleSaleStaus.cnt > 0) {
        const currentSaleCount = (cartMobx.cartResult.allScaleNoToCntMap[theProductNo + '_' + theScaleNo] || 0) + this.doCombo.cnt - cartItemCnt
        if (currentSaleCount > scaleSaleStaus.cnt) {
          return restaurantConfig.app === 'pos' ? currentSaleCount - productSaleStaus.cnt : 9001
        }
      }
    }
    return 0
  }

  /** 设置打包/堂食 */
  switchTogo () {
    if (cartMobx.info.eatType !== 'dinein') {
      return
    }
    this.doCombo.isTogo = this.doCombo.isTogo ? 0 : 1
    this.doCombo.productGroups?.forEach(group => {
      group.productGroupDetails?.forEach(product => {
        product.isTogo = this.doCombo.isTogo
      })
    })
  }

  /** 设置手工折扣，因为当前是暂存状态估存放在doProduct，实际使用后会存储到discountMobx中 */
  setDiscountPlan (plan?: IDiscountPlan | null) {
    this.doCombo.itemPlan = plan || null
  }

  /** 设置免税 */
  setTaxFee () {
    this.doCombo.isTaxFree = !this.doCombo.isTaxFree
  }

  /** 设置预约时间 */
  setStartTime (startTime?: string, pickTimeRange?: any) {
    this.doCombo.startTime = startTime
    this.doCombo.pickTimeRange = pickTimeRange
  }
}

export default new DoCombo()
